import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search, ZoomIn, ZoomOut, Maximize2, Plus, X, Loader2,
  AlertTriangle, ChevronDown, ChevronRight, BarChart3,
  Zap, FileText, BookOpen, ExternalLink, Layers, Target,
  ArrowRight, Printer,
} from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

/* ─── constants ─── */
const DISC_COLORS = {
  "Higher Education": { main: "#1a6fc4", light: "#dbeafe", ring: "#93c5fd" },
  "K-12": { main: "#059669", light: "#d1fae5", ring: "#6ee7b7" },
  Workforce: { main: "#d97706", light: "#fef3c7", ring: "#fcd34d" },
  "Media Library": { main: "#7c3aed", light: "#ede9fe", ring: "#c4b5fd" },
  Business: { main: "#0369a1", light: "#e0f2fe", ring: "#7dd3fc" },
  STEM: { main: "#dc2626", light: "#fee2e2", ring: "#fca5a5" },
  "Health Sciences": { main: "#be185d", light: "#fce7f3", ring: "#f9a8d4" },
  Arts: { main: "#ca8a04", light: "#fef9c3", ring: "#fde047" },
  "Social Sciences": { main: "#16a34a", light: "#dcfce7", ring: "#86efac" },
  Education: { main: "#0891b2", light: "#cffafe", ring: "#67e8f9" },
};
const fallbackColor = { main: "#6b7280", light: "#f3f4f6", ring: "#d1d5db" };
const getDiscColor = (name) => DISC_COLORS[name] || fallbackColor;

const SEVERITY_CLS = {
  high: "bg-red-50 border-red-200 text-red-700",
  medium: "bg-amber-50 border-amber-200 text-amber-700",
  low: "bg-emerald-50 border-emerald-200 text-emerald-700",
};

/* ─── Discipline Tree Panel ─── */
const DisciplineTree = ({ disciplines, programs, selected, onSelect }) => {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (name) => setCollapsed((p) => ({ ...p, [name]: !p[name] }));

  // Group programs by discipline name (match connection source)
  const discPrograms = {};
  disciplines.forEach((d) => { discPrograms[d.name] = []; });
  // Programs are listed as projects, approximate grouping by discipline tag match
  programs.forEach((p) => {
    const matched = disciplines.find((d) =>
      (p.name || "").toLowerCase().includes(d.name.toLowerCase().split(" ")[0]) ||
      (p.description || "").toLowerCase().includes(d.name.toLowerCase().split(" ")[0])
    );
    if (matched) discPrograms[matched.name]?.push(p);
  });

  const totalTitles = disciplines.reduce((s, d) => s + d.asset_count, 0);

  return (
    <div className="flex flex-col h-full" data-testid="discipline-tree">
      <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
        <span className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider">Disciplines & Programs</span>
        <button onClick={() => {
          const allCollapsed = disciplines.every((d) => collapsed[d.name]);
          const next = {};
          disciplines.forEach((d) => { next[d.name] = !allCollapsed; });
          setCollapsed(next);
        }} className="text-[10px] font-semibold text-foreground/50 hover:text-foreground/60">
          {disciplines.every((d) => collapsed[d.name]) ? "Expand All" : "Collapse All"}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
        {/* All Disciplines root */}
        <button
          onClick={() => onSelect(null, null)}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-semibold ${
            !selected ? "bg-[#014d7d]/5 text-[#014d7d]" : "text-foreground/50 hover:bg-foreground/[0.02]"
          }`}
          data-testid="tree-all"
        >
          <Layers className="w-3 h-3" /> All Disciplines
          <span className="ml-auto text-[10px] text-foreground/50">{totalTitles}</span>
        </button>

        {disciplines?.map((disc) => {
          const dc = getDiscColor(disc.name);
          const isOpen = !collapsed[disc.name];
          const progs = discPrograms[disc.name] || [];
          return (
            <div key={disc.name}>
              <button
                onClick={() => { toggle(disc.name); onSelect("discipline", disc.name); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-semibold ${
                  selected?.type === "discipline" && selected?.name === disc.name
                    ? "bg-[#014d7d]/5 text-[#014d7d]" : "text-foreground/60 hover:bg-foreground/[0.02]"
                }`}
                data-testid={`tree-disc-${disc.name}`}
              >
                {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: dc.main }} />
                <span className="truncate">{disc.name}</span>
                <span className="ml-auto text-[10px] text-foreground/45">{disc.asset_count}</span>
              </button>
              {isOpen && progs.length > 0 && (
                <div className="ml-6 mt-0.5 space-y-0.5">
                  {progs?.map((p) => (
                    <button key={p.id}
                      onClick={() => onSelect("program", p.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1 rounded-lg text-[11px] ${
                        selected?.type === "program" && selected?.name === p.id
                          ? "bg-[#014d7d]/5 text-[#014d7d] font-semibold" : "text-foreground/40 hover:bg-foreground/[0.02]"
                      }`}
                      data-testid={`tree-prog-${p.id}`}
                    >
                      <span className="truncate">{p.name}</span>
                      <span className="ml-auto text-[10px] text-foreground/45">{p.artifact_count || 0}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-t border-border/40">
        <p className="text-[9px] font-bold text-foreground/45 uppercase tracking-wider mb-2">Legend</p>
        <div className="space-y-1.5">
          {[
            { label: "Hierarchy", style: "border-t border-dashed border-foreground/20", desc: "Parent-child" },
            { label: "Reuse Link", style: "border-t-2 border-[#014d7d]", desc: "Content shared" },
            { label: "Coverage Gap", style: "border-t-2 border-red-400 border-dashed", desc: "Missing content" },
          ]?.map((l) => (
            <div key={l.label} className="flex items-center gap-2">
              <div className={`w-6 ${l.style}`} />
              <span className="text-[9px] text-foreground/50 font-medium">{l.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Graph View ─── */
const GraphView = ({ disciplines, programs, connections, gaps, zoom, pan, onSelectNode, selectedNode }) => {
  const svgRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [localPan, setLocalPan] = useState(pan);

  useEffect(() => setLocalPan(pan), [pan]);

  const handleMouseDown = (e) => {
    if (e.target.tagName === "svg" || e.target.dataset.bg) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - localPan.x, y: e.clientY - localPan.y });
    }
  };
  const handleMouseMove = (e) => {
    if (isDragging) setLocalPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setIsDragging(false);

  const cx = 450, cy = 280;
  const discAngle = (2 * Math.PI) / Math.max(disciplines.length, 1);
  const discRadius = 200;
  const discPositions = disciplines?.map((d, i) => ({
    ...d,
    x: cx + Math.cos(discAngle * i - Math.PI / 2) * discRadius,
    y: cy + Math.sin(discAngle * i - Math.PI / 2) * discRadius,
  }));

  // Place programs further out, orbiting their discipline
  const progPositions = [];
  const discProgMap = {};
  disciplines.forEach((d) => { discProgMap[d.name] = []; });
  programs.forEach((p) => {
    const match = disciplines.find((d) =>
      (p.name || "").toLowerCase().includes(d.name.toLowerCase().split(" ")[0]) ||
      (p.description || "").toLowerCase().includes(d.name.toLowerCase().split(" ")[0])
    );
    if (match) discProgMap[match.name].push(p);
  });

  disciplines.forEach((d, di) => {
    const progs = discProgMap[d.name] || [];
    const dp = discPositions[di];
    const baseAngle = discAngle * di - Math.PI / 2;
    progs.forEach((p, pi) => {
      const spread = Math.PI * 0.6;
      const a = baseAngle - spread / 2 + (spread / Math.max(progs.length - 1, 1)) * pi;
      const r = 85;
      progPositions.push({ ...p, x: dp.x + Math.cos(a) * r, y: dp.y + Math.sin(a) * r, parentDisc: d.name });
    });
  });

  // Gap connections (dashed red between disciplines that have gaps)
  const gapDiscs = gaps?.filter((g) => g.discipline)?.map((g) => g.discipline);

  return (
    <svg ref={svgRef} width="100%" height="100%"
      className={isDragging ? "cursor-grabbing" : "cursor-grab"}
      onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
      data-testid="knowledge-graph-svg"
    >
      <rect data-bg="true" width="100%" height="100%" fill="transparent" />
      <g transform={`translate(${localPan.x},${localPan.y}) scale(${zoom})`}>
        {/* Lines: disc → center */}
        {discPositions?.map((d, i) => (
          <line key={`c-${i}`} x1={cx} y1={cy} x2={d.x} y2={d.y}
            stroke={getDiscColor(d.name).ring} strokeWidth={1.5} strokeDasharray="6,4" opacity={0.5} />
        ))}

        {/* Lines: prog → disc */}
        {progPositions?.map((p, i) => {
          const parent = discPositions.find((d) => d.name === p.parentDisc);
          if (!parent) return null;
          return <line key={`p-${i}`} x1={parent.x} y1={parent.y} x2={p.x} y2={p.y}
            stroke={getDiscColor(p.parentDisc).ring} strokeWidth={1} opacity={0.3} />;
        })}

        {/* Gap indicators between disciplines */}
        {gapDiscs?.map((gd, i) => {
          const dp = discPositions.find((d) => d.name === gd);
          if (!dp) return null;
          return (
            <g key={`gap-${i}`}>
              <line x1={cx} y1={cy} x2={dp.x} y2={dp.y} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4,4" opacity={0.4} />
              <text x={(cx + dp.x) / 2} y={(cy + dp.y) / 2 - 8} textAnchor="middle" fontSize="8" fill="#ef4444" fontWeight="700">GAP</text>
            </g>
          );
        })}

        {/* Center node: Content Catalog */}
        <g className="cursor-pointer" onClick={() => onSelectNode(null, null)}>
          <circle cx={cx} cy={cy} r={40} fill="white" stroke="#014d7d" strokeWidth={2.5} />
          <circle cx={cx} cy={cy} r={36} fill="#014d7d" opacity={0.05} />
          <text x={cx} y={cy - 6} textAnchor="middle" fontSize="9" fontWeight="700" fill="#014d7d">Content</text>
          <text x={cx} y={cy + 6} textAnchor="middle" fontSize="9" fontWeight="700" fill="#014d7d">Catalog</text>
          <text x={cx} y={cy + 18} textAnchor="middle" fontSize="8" fill="#014d7d" opacity={0.5}>
            {disciplines.reduce((s, d) => s + d.asset_count, 0)} titles
          </text>
        </g>

        {/* Discipline nodes */}
        {discPositions?.map((d) => {
          const dc = getDiscColor(d.name);
          const isSelected = selectedNode?.type === "discipline" && selectedNode?.name === d.name;
          return (
            <g key={d.name} className="cursor-pointer" onClick={() => onSelectNode("discipline", d.name)}
              data-testid={`graph-node-${d.name}`}>
              <circle cx={d.x} cy={d.y} r={isSelected ? 38 : 34} fill={isSelected ? dc.light : "white"}
                stroke={dc.main} strokeWidth={isSelected ? 2.5 : 1.5} />
              <text x={d.x} y={d.y - 5} textAnchor="middle" fontSize="9" fontWeight="700" fill={dc.main}>
                {d.name.length > 16 ? d.name.slice(0, 14) + "…" : d.name}
              </text>
              <text x={d.x} y={d.y + 7} textAnchor="middle" fontSize="8" fill="#64748b">
                {d.asset_count} titles
              </text>
              {d.total_issues > 10 && (
                <g>
                  <circle cx={d.x + 24} cy={d.y - 24} r={8} fill="#ef4444" />
                  <text x={d.x + 24} y={d.y - 21} textAnchor="middle" fontSize="7" fill="white" fontWeight="700">{d.total_issues}</text>
                </g>
              )}
            </g>
          );
        })}

        {/* Program nodes (smaller) */}
        {progPositions?.map((p) => {
          const dc = getDiscColor(p.parentDisc);
          const isSelected = selectedNode?.type === "program" && selectedNode?.name === p.id;
          return (
            <g key={p.id} className="cursor-pointer" onClick={() => onSelectNode("program", p.id)}
              data-testid={`graph-prog-${p.id}`}>
              <circle cx={p.x} cy={p.y} r={isSelected ? 22 : 18} fill={isSelected ? dc.light : "white"}
                stroke={isSelected ? dc.main : "#cbd5e1"} strokeWidth={isSelected ? 2 : 1} />
              <text x={p.x} y={p.y - 2} textAnchor="middle" fontSize="7" fontWeight="600" fill="#334155">
                {(p.name || "").length > 14 ? (p.name || "").slice(0, 12) + "…" : p.name}
              </text>
              <text x={p.x} y={p.y + 7} textAnchor="middle" fontSize="6" fill="#94a3b8">{p.artifact_count || 0}</text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};

/* ─── Coverage Heatmap ─── */
const CoverageView = ({ disciplines }) => {
  const modalities = ["Textbook", "Lecture Video", "Assessment", "Interactive", "Audio", "Media Pack"];
  return (
    <div className="p-6 overflow-auto" data-testid="coverage-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-heading font-bold text-foreground">Coverage Heatmap</h3>
        <button className="text-[10px] font-semibold px-3 py-1.5 rounded-lg border border-border/40 text-foreground/40 flex items-center gap-1.5 hover:text-foreground/60"><Printer className="w-3 h-3" /> Print</button>
      </div>
      <div className="overflow-x-auto border border-border/40 rounded-xl">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="bg-secondary/40">
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-foreground/40 uppercase tracking-wider">Discipline</th>
              {modalities?.map((m) => (
                <th key={m} className="px-3 py-2.5 text-center text-[10px] font-bold text-foreground/40 uppercase tracking-wider">{m}</th>
              ))}
              <th className="px-3 py-2.5 text-center text-[10px] font-bold text-foreground/40 uppercase tracking-wider">Overall</th>
            </tr>
          </thead>
          <tbody>
            {disciplines?.map((d) => {
              const dc = getDiscColor(d.name);
              const formats = d.formats || [];
              const assetsByType = {};
              (d.assets || []).forEach((a) => {
                const t = a.format || "PDF";
                if (!assetsByType[t]) assetsByType[t] = 0;
                assetsByType[t]++;
              });
              return (
                <tr key={d.name} className="border-t border-border/20 hover:bg-secondary/20">
                  <td className="px-4 py-2.5 flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: dc.main }} />
                    <span className="text-xs font-semibold text-foreground">{d.name}</span>
                  </td>
                  {modalities?.map((mod) => {
                    // Map modality to format keys
                    const fmtMap = { "Textbook": ["PDF", "EPUB"], "Lecture Video": ["Video (MP4)"], "Assessment": ["SCORM"], "Interactive": ["SCORM"], "Audio": ["Audio (MP3)"], "Media Pack": ["Media Pack (ZIP)"] };
                    const count = (fmtMap[mod] || []).reduce((s, f) => s + (assetsByType[f] || 0), 0);
                    const pct = d.asset_count > 0 ? Math.round((count / d.asset_count) * 100) : 0;
                    const bg = count === 0 ? "bg-gray-50 text-gray-300" : pct >= 30 ? "bg-emerald-50 text-emerald-700" : pct >= 15 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700";
                    return (
                      <td key={mod} className="px-3 py-2.5 text-center">
                        <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-md ${bg}`}>
                          {count > 0 ? count : "—"}
                        </span>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-xs font-bold ${d.avg_a11y >= 75 ? "text-emerald-600" : d.avg_a11y >= 50 ? "text-amber-600" : "text-red-600"}`}>{d.avg_a11y}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 mt-3">
        <span className="text-[9px] font-bold text-foreground/45 uppercase">Legend:</span>
        {[{ label: "< 50%", cls: "bg-red-50 text-red-600" }, { label: "50-74%", cls: "bg-amber-50 text-amber-600" }, { label: "≥ 75%", cls: "bg-emerald-50 text-emerald-600" }]?.map((l) => (
          <div key={l.label} className="flex items-center gap-1">
            <div className={`w-3 h-3 rounded ${l.cls}`} />
            <span className="text-[9px] text-foreground/50 font-medium">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Reuse Map ─── */
const ReuseMapView = ({ disciplines }) => (
  <div className="p-6 overflow-auto" data-testid="reuse-map-view">
    <h3 className="text-sm font-heading font-bold text-foreground mb-4">Cross-Discipline Reuse Matrix</h3>
    <div className="overflow-x-auto border border-border/40 rounded-xl">
      <table className="w-full min-w-[500px]">
        <thead>
          <tr className="bg-secondary/40">
            <th className="px-4 py-2.5 text-left text-[10px] font-bold text-foreground/40 uppercase">Source → Target</th>
            {disciplines?.map((d) => {
              const dc = getDiscColor(d.name);
              return <th key={d.name} className="px-3 py-2.5 text-center text-[10px] font-bold" style={{ color: dc.main }}>{d.name.split(" ")[0]}</th>;
            })}
          </tr>
        </thead>
        <tbody>
          {disciplines?.map((source) => {
            const sc = getDiscColor(source.name);
            // Simulated reuse potential based on shared tags
            const sourceTags = new Set(source.tags || []);
            return (
              <tr key={source.name} className="border-t border-border/20">
                <td className="px-4 py-2.5 flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: sc.main }} />
                  <span className="text-xs font-semibold text-foreground">{source.name}</span>
                </td>
                {disciplines?.map((target) => {
                  if (source.name === target.name) {
                    return <td key={target.name} className="px-3 py-2.5 text-center"><span className="text-[10px] text-foreground/10">—</span></td>;
                  }
                  const targetTags = new Set(target.tags || []);
                  const shared = [...sourceTags]?.filter((t) => targetTags.has(t)).length;
                  const potential = Math.min(shared * 3 + Math.floor(Math.random() * 5), source.asset_count);
                  const bg = potential > 5 ? "bg-[#014d7d]/10 text-[#014d7d] font-bold" : potential > 0 ? "bg-foreground/[0.03] text-foreground/40" : "text-foreground/10";
                  return (
                    <td key={target.name} className="px-3 py-2.5 text-center">
                      <span className={`inline-block text-[10px] px-2 py-0.5 rounded-md ${bg}`}>{potential > 0 ? potential : "—"}</span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <p className="text-[10px] text-foreground/50 mt-3 italic">Numbers show estimated reusable content objects from source to target discipline.</p>
  </div>
);

/* ─── Right Detail Panel ─── */
const DetailPanel = ({ disciplines, programs, gaps, selectedNode, navigate, onCreateTask, onClose }) => {
  const selDisc = selectedNode?.type === "discipline" ? disciplines.find((d) => d.name === selectedNode.name) : null;
  const selProg = selectedNode?.type === "program" ? programs.find((p) => p.id === selectedNode.name) : null;

  if (!selDisc && !selProg) {
    // Show all disciplines summary + gaps
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <div className="px-4 py-3 border-b border-border/40">
          <h3 className="text-xs font-heading font-bold text-foreground">All Disciplines</h3>
        </div>
        <div className="px-4 py-3 space-y-2">
          {disciplines?.map((d) => {
            const dc = getDiscColor(d.name);
            return (
              <div key={d.name} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-foreground/[0.02]">
                <div className="w-3 h-3 rounded-full" style={{ background: dc.main }} />
                <span className="text-xs font-semibold text-foreground flex-1">{d.name}</span>
                <span className="text-[10px] text-foreground/50">{d.asset_count}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${d.avg_a11y >= 75 ? "bg-emerald-50 text-emerald-600" : d.avg_a11y >= 50 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`}>{d.avg_a11y}%</span>
              </div>
            );
          })}
        </div>

        {gaps.length > 0 && (
          <div className="px-4 py-3 border-t border-border/40">
            <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-2">Coverage Gaps</p>
            <div className="space-y-2">
              {gaps.slice(0, 6)?.map((g, i) => (
                <div key={i} className={`px-3 py-2.5 rounded-xl border ${SEVERITY_CLS[g.severity] || SEVERITY_CLS.medium}`}>
                  <div className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold">{g.severity?.toUpperCase()}</p>
                      <p className="text-[10px] leading-relaxed">{g.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <button onClick={() => onCreateTask(g.suggested_action)} className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-white/60 hover:bg-white border border-current/20 flex items-center gap-1">
                      <Zap className="w-2.5 h-2.5" /> Commission
                    </button>
                    <button className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-white/60 hover:bg-white border border-current/20">Coverage View</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (selDisc) {
    const dc = getDiscColor(selDisc.name);
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ background: dc.main }} />
            <h3 className="text-xs font-heading font-bold text-foreground">{selDisc.name}</h3>
          </div>
          <button onClick={onClose} className="text-foreground/45 hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
        </div>
        <div className="px-4 py-3 grid grid-cols-2 gap-2 border-b border-border/40">
          {[
            { label: "Total Titles", value: selDisc.asset_count },
            { label: "Content Assets", value: selDisc.asset_count },
            { label: "Avg A11y", value: `${selDisc.avg_a11y}%` },
            { label: "Issues", value: selDisc.total_issues },
          ]?.map((m) => (
            <div key={m.label} className="p-2.5 rounded-lg bg-foreground/[0.02]">
              <p className="text-base font-heading font-bold text-foreground">{m.value}</p>
              <p className="text-[9px] text-foreground/50">{m.label}</p>
            </div>
          ))}
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-2">Assets ({selDisc.assets?.length || 0})</p>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {(selDisc.assets || [])?.map((a) => (
              <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-foreground/[0.02]">
                <FileText className="w-3 h-3 text-foreground/45 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-foreground truncate">{a.name}</p>
                  <p className="text-[9px] text-foreground/45">{a.format} · {a.a11y}%</p>
                </div>
                <div className={`w-2 h-2 rounded-full ${a.status === "good" ? "bg-emerald-500" : a.status === "warning" ? "bg-amber-400" : "bg-red-500"}`} />
              </div>
            ))}
          </div>
        </div>
        {(selDisc.tags || []).length > 0 && (
          <div className="px-4 py-3 border-t border-border/40">
            <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-2">Tags</p>
            <div className="flex gap-1 flex-wrap">{selDisc.tags?.map((t) => <span key={t} className="text-[9px] px-2 py-0.5 rounded-full bg-foreground/[0.04] text-foreground/40 font-medium">{t}</span>)}</div>
          </div>
        )}
        <div className="px-4 py-3 border-t border-border/40 mt-auto">
          <button onClick={() => onCreateTask(`Remediate ${selDisc.name} content gaps`)} className="w-full text-xs font-semibold px-3 py-2 rounded-xl border border-[#014d7d]/20 text-[#014d7d] hover:bg-[#014d7d]/5 flex items-center gap-2 justify-center" data-testid="create-task-disc">
            <Plus className="w-3.5 h-3.5" /> Create Task
          </button>
        </div>
      </div>
    );
  }

  if (selProg) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
          <h3 className="text-xs font-heading font-bold text-foreground truncate">{selProg.name}</h3>
          <button onClick={onClose} className="text-foreground/45 hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
        </div>
        <div className="px-4 py-3 border-b border-border/40">
          <p className="text-xs text-foreground/40 mb-2">{selProg.description}</p>
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-[#014d7d]" style={{ width: `${selProg.progress || 0}%` }} /></div>
          <p className="text-[10px] text-foreground/50 mt-1">{selProg.progress || 0}% complete · {selProg.artifact_count} artifacts</p>
        </div>
        {Object.keys(selProg.artifact_categories || {}).length > 0 && (
          <div className="px-4 py-3 border-b border-border/40">
            <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-2">Artifact Categories</p>
            {Object.entries(selProg.artifact_categories)?.map(([cat, arts]) => (
              <div key={cat} className="flex items-center justify-between px-2 py-1.5 rounded-lg">
                <span className="text-[11px] font-medium text-foreground capitalize">{cat}</span>
                <span className="text-[10px] text-foreground/50">{arts.length}</span>
              </div>
            ))}
          </div>
        )}
        <div className="px-4 py-3 mt-auto space-y-2">
          <button onClick={() => navigate(`/projects/${selProg.id}`)} className="w-full text-xs font-semibold px-3 py-2 rounded-xl bg-[#014d7d] text-white hover:bg-[#013d66] flex items-center gap-2 justify-center">
            <ExternalLink className="w-3.5 h-3.5" /> Open Project
          </button>
          <button onClick={() => onCreateTask(`Task for "${selProg.name}"`)} className="w-full text-xs font-semibold px-3 py-2 rounded-xl border border-border/60 text-foreground/50 hover:text-foreground flex items-center gap-2 justify-center">
            <Plus className="w-3.5 h-3.5" /> Create Task
          </button>
        </div>
      </div>
    );
  }

  return null;
};

/* ═══════════════ Main Knowledge Map ═══════════════ */
export const KnowledgeGraph = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState("graph");
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [search, setSearch] = useState("");
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskProject, setTaskProject] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [mapRes, projRes] = await Promise.all([
          axios.get(`${API}/knowledge-map`, { withCredentials: true }),
          axios.get(`${API}/projects`, { withCredentials: true }),
        ]);
        setData(mapRes.data && typeof mapRes.data === 'object' && !Array.isArray(mapRes.data) ? mapRes.data : null);
        setProjects(Array.isArray(projRes.data) ? projRes.data : []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSelectNode = useCallback((type, name) => {
    if (type === null) { setSelectedNode(null); return; }
    setSelectedNode((prev) => prev?.type === type && prev?.name === name ? null : { type, name });
  }, []);

  const handleCreateTask = useCallback((title) => {
    setTaskTitle(title);
    setShowTaskModal(true);
  }, []);

  const submitTask = async () => {
    if (!taskTitle.trim() || !taskProject || creating) return;
    setCreating(true);
    try {
      await axios.post(`${API}/projects/${taskProject}/tasks`, { title: taskTitle, description: "Created from Knowledge Map", stage: "audit" }, { withCredentials: true });
      setShowTaskModal(false);
      setTaskTitle("");
      setTaskProject("");
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-foreground/45" /></div>;

  const disciplines = data?.disciplines || [];
  const programs = data?.programs || [];
  const connections = data?.connections || [];
  const gaps = data?.gaps || [];
  const summary = data?.summary || {};

  return (
    <div className="flex flex-col h-full" data-testid="knowledge-graph-page">
      {/* Top bar with global metrics */}
      <div className="px-4 py-2.5 bg-white border-b border-border/40 flex items-center gap-3 shrink-0">
        {/* View tabs */}
        <div className="flex bg-[#f0f4f8] rounded-full p-0.5 border border-border/40">
          {["graph", "coverage", "reuse"]?.map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`text-[10px] font-semibold px-3.5 py-1.5 rounded-full capitalize ${view === v ? "bg-white text-foreground shadow-sm" : "text-foreground/50"}`}
              data-testid={`view-${v}`}>{v === "reuse" ? "Reuse Map" : v === "coverage" ? "Coverage" : "Graph"}</button>
          ))}
        </div>

        {/* Global metrics */}
        <div className="flex items-center gap-3 ml-2">
          {[
            { label: "Total Titles", value: summary.total_assets || 0 },
            { label: "Published", value: "42" },
            { label: "Gaps / Issues", value: summary.total_gaps || 0, warn: true },
            { label: "Reuse Potential", value: "12%" },
          ]?.map((m) => (
            <div key={m.label} className="text-center px-2">
              <p className={`text-sm font-heading font-bold ${m.warn ? "text-amber-600" : "text-foreground"}`}>{m.value}</p>
              <p className="text-[8px] text-foreground/45 font-semibold uppercase tracking-wider">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="flex-1" />

        {/* Search */}
        <div className="relative w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-foreground/45" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
            className="w-full h-8 pl-8 pr-3 text-[11px] border border-border/50 rounded-lg outline-none placeholder:text-foreground/45"
            data-testid="graph-search" />
        </div>

        {view === "graph" && (
          <>
            <div className="flex items-center border border-border/50 rounded-lg overflow-hidden">
              <button onClick={() => setZoom((z) => Math.min(z + 0.15, 2.5))} className="w-7 h-7 flex items-center justify-center text-foreground/50 hover:text-foreground"><ZoomIn className="w-3 h-3" /></button>
              <span className="text-[9px] font-bold text-foreground/50 px-1 w-8 text-center">{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom((z) => Math.max(z - 0.15, 0.4))} className="w-7 h-7 flex items-center justify-center text-foreground/50 hover:text-foreground"><ZoomOut className="w-3 h-3" /></button>
            </div>
            <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="w-7 h-7 flex items-center justify-center text-foreground/50 hover:text-foreground border border-border/50 rounded-lg"><Maximize2 className="w-3 h-3" /></button>
          </>
        )}

        <span className="text-[9px] text-foreground/45 italic">Click any node to explore. Drag to pan.</span>
      </div>

      {/* Main Layout: Left Tree + Canvas + Right Detail */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Discipline Tree */}
        <div className="w-56 shrink-0 bg-white border-r border-border/40 overflow-hidden">
          <DisciplineTree
            disciplines={disciplines}
            programs={programs}
            selected={selectedNode}
            onSelect={handleSelectNode}
          />
        </div>

        {/* Center: Canvas */}
        <div className="flex-1 bg-[#f8f9fb] overflow-hidden relative">
          {view === "graph" && (
            <GraphView
              disciplines={disciplines} programs={programs} connections={connections} gaps={gaps}
              zoom={zoom} pan={pan} onSelectNode={handleSelectNode} selectedNode={selectedNode}
            />
          )}
          {view === "coverage" && <CoverageView disciplines={disciplines} />}
          {view === "reuse" && <ReuseMapView disciplines={disciplines} />}
        </div>

        {/* Right: Detail Panel */}
        <div className="w-64 shrink-0 bg-white border-l border-border/40 overflow-hidden">
          <DetailPanel
            disciplines={disciplines} programs={programs} gaps={gaps}
            selectedNode={selectedNode} navigate={navigate}
            onCreateTask={handleCreateTask}
            onClose={() => setSelectedNode(null)}
          />
        </div>
      </div>

      {/* Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 backdrop-blur-sm" data-testid="task-modal-overlay">
          <div className="bg-white rounded-2xl shadow-elevated border border-border/60 w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-sm text-foreground">Create Task</h3>
              <button onClick={() => setShowTaskModal(false)} className="text-foreground/50 hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Task title…"
                className="w-full h-10 px-3 text-sm border border-border/60 rounded-xl outline-none" data-testid="task-modal-title" />
              <select value={taskProject} onChange={(e) => setTaskProject(e.target.value)}
                className="w-full h-10 px-3 text-sm border border-border/60 rounded-xl outline-none bg-white text-foreground/60" data-testid="task-modal-project">
                <option value="">Select project…</option>
                {projects?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <div className="flex gap-2">
                <button onClick={submitTask} disabled={!taskTitle.trim() || !taskProject || creating}
                  className="text-xs font-semibold px-4 py-2 rounded-xl bg-[#014d7d] text-white disabled:opacity-40 flex items-center gap-1.5" data-testid="task-modal-submit">
                  {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Create
                </button>
                <button onClick={() => setShowTaskModal(false)} className="text-xs font-semibold px-4 py-2 rounded-xl border border-border/60 text-foreground/50">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
